import {
  API_ROUTES,
  ccaRowSchema,
  fiscalYearStatusSchema,
  t5018RowSchema,
  type CcaInputRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

export const yearEndKeys = {
  all: (organizationId: string) => ['year-end', organizationId] as const,
  status: (organizationId: string, fiscalYear: number) =>
    ['year-end', organizationId, 'status', fiscalYear] as const,
  t5018: (organizationId: string, fiscalYear: number) =>
    ['year-end', organizationId, 't5018', fiscalYear] as const,
  cca: (organizationId: string, fiscalYear: number) =>
    ['year-end', organizationId, 'cca', fiscalYear] as const,
};

export const yearEndStatusQuery = (organizationId: string, fiscalYear: number) =>
  queryOptions({
    queryKey: yearEndKeys.status(organizationId, fiscalYear),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.yearEnd.status(organizationId)}?fiscalYear=${String(fiscalYear)}`,
        schema: fiscalYearStatusSchema,
        signal,
      }),
    staleTime: 15_000,
  });

export const t5018Query = (organizationId: string, fiscalYear: number) =>
  queryOptions({
    queryKey: yearEndKeys.t5018(organizationId, fiscalYear),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.yearEnd.t5018(organizationId)}?fiscalYear=${String(fiscalYear)}`,
        schema: z.array(t5018RowSchema),
        signal,
      }),
    staleTime: 15_000,
  });

export const ccaQuery = (organizationId: string, fiscalYear: number) =>
  queryOptions({
    queryKey: yearEndKeys.cca(organizationId, fiscalYear),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.yearEnd.cca(organizationId)}?fiscalYear=${String(fiscalYear)}`,
        schema: z.array(ccaRowSchema),
        signal,
      }),
    staleTime: 15_000,
  });

export const setFiscalYearEnd = (organizationId: string, month: number) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.yearEnd.fiscalYearEnd(organizationId),
    schema: z.undefined(),
    body: { month },
  });

export const setCcaInput = (organizationId: string, body: CcaInputRequest) =>
  apiClient.request({
    method: 'PATCH',
    path: API_ROUTES.yearEnd.cca(organizationId),
    schema: z.array(ccaRowSchema),
    body,
  });

export const postCca = (organizationId: string, fiscalYear: number) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.yearEnd.postCca(organizationId),
    schema: z.object({ entryId: z.string().nullable() }),
    body: { fiscalYear },
  });

export const closeFiscalYear = (organizationId: string, fiscalYear: number) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.yearEnd.close(organizationId),
    schema: z.object({ entryId: z.string().nullable() }),
    body: { fiscalYear },
  });

export const reopenFiscalYear = (organizationId: string, fiscalYear: number, reason: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.yearEnd.reopen(organizationId),
    schema: z.undefined(),
    body: { fiscalYear, reason },
  });
